import React, { useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext } from "@mui/lab";

import "./Section.css";
import SuccessAlert from "../components/success-alert/SuccessAlert";
import ErrorAlert from "../components/error-alert/ErrorAlert";
import useLanguages from "../hooks/useLanguages";
import { API, SERVER_DOMAIN } from "../assets/staticData";

export default function Sitemap({user}) {
  const langs = useLanguages();

  const [value, setValue] = useState("0");
  const [success, setSuccess] = useState(false);
  const [fail, setFail] = useState(false);
  const [loading, setLoading] = useState(false);

  let userToken = localStorage.getItem("userToken");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let tabStyle = { textTransform: "capitalize", fontWeight: "500" };

  const generateSitemapXml = (routes) => {
    const BASE_URL = "https://mishovmarkets.com";
    const now = new Date();

    const xmlHeader = `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">\n`;
    const xmlFooter = `</urlset>`;

    const xmlBody = routes
      .map((route) => {
        let urlEntry = `
        <url>
          <loc>${BASE_URL}${route.url}</loc>
          <lastmod>${now.toISOString().replace(/\.\d{3}Z$/, "+00:00")}</lastmod>
          <changefreq>${route.changefreq}</changefreq>
          <priority>${route.priority}</priority>`;

        if (route.image) {
          urlEntry += `
          <image:image>
            <image:loc>${route.image.loc}</image:loc>
            <image:title>${route.image.title}</image:title>
          </image:image>`;
        }

        urlEntry += `</url>`;
        return urlEntry;
      })
      .join("\n");

    return xmlHeader + xmlBody + "\n" + xmlFooter;
  };

  const uploadSitemap = async (xmlContent) => {
    const blob = new Blob([xmlContent], { type: "application/xml" });
    const formData = new FormData();
    formData.append("files", blob, "sitemap.xml");

    try {
      const response = await fetch(`${API}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const fileUrl = data[0].url;
        downloadSitemap(SERVER_DOMAIN + fileUrl);
        setSuccess(true);
        setFail(false);
      } else {
        const errorData = await response.json();
        console.error("Failed to upload sitemap:", errorData);
        setFail(true);
        setSuccess(false);
      }
    } catch (error) {
      console.error("Error uploading sitemap:", error);
      setFail(true);
      setSuccess(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/ /g, "-")
      .replace(/[^\w-]+/g, "");
  };

  const generateDynamicRoutesFromPosts = (posts) => {
    return posts.map((post) => {
      const title = post.attributes.title;
      const slug = generateSlug(title);
      const imageUrl = post.attributes.image?.data?.attributes?.url;

      return {
        url: `/post/${slug}?id=${post.id}`,
        changefreq: "weekly",
        priority: 0.6,
        image: imageUrl
          ? {
              loc: SERVER_DOMAIN + imageUrl,
              title: post.attributes.title,
            }
          : null,
      };
    });
  };

  const downloadSitemap = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "sitemap.xml";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const generate = async (index) => {
    setLoading(true);

    const staticRoutes = [
      { url: "/", changefreq: "daily", priority: 1.0 },
      { url: "/About", changefreq: "monthly", priority: 0.8 },
      { url: "/Platforms", changefreq: "monthly", priority: 0.8 },
      { url: "/Profit-Loss-Calculator", changefreq: "monthly", priority: 0.8 },
      { url: "/Margin-Calculator", changefreq: "monthly", priority: 0.8 },
      { url: "/Spread-Calculator", changefreq: "monthly", priority: 0.8 },
      { url: "/Help-Center", changefreq: "monthly", priority: 0.8 },
      { url: "/Blog", changefreq: "weekly", priority: 0.8 },
      { url: "/Privacy-Policy", changefreq: "monthly", priority: 0.8 },
      { url: "/Account-Types", changefreq: "monthly", priority: 0.8 },
      { url: "/Market", changefreq: "monthly", priority: 0.8 },
      { url: "/Promotion", changefreq: "monthly", priority: 0.8 },
      { url: "/FAQ", changefreq: "monthly", priority: 0.8 },
    ];

    const getPosts = await fetch(`${API}/articles?populate=*`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    });

    const posts = await getPosts.json();

    const getAnalysis = await fetch(`${API}/analyses?populate=*`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    });

    const analysis = await getAnalysis.json();

    const dynamicRoutes = generateDynamicRoutesFromPosts(posts.data);
    const analysisRoutes = generateDynamicRoutesFromPosts(analysis.data);

    const routes = [...staticRoutes, ...dynamicRoutes, ...analysisRoutes];

    const xmlContent = generateSitemapXml(routes);

    await uploadSitemap(xmlContent);

    setLoading(false);
  };

  return (
    <TabContext value={value}>
      <Box sx={{ width: "100%" }}>
        <h3 style={{ textTransform: "capitalize" }}>Sitemap Settings</h3>
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="secondary tabs"
        >
          {langs?.map((item, index) => {
            return (
              <Tab
                key={index}
                sx={tabStyle}
                value={index.toString()}
                label={item.attributes.language}
                icon={
                  <img
                    src={
                      SERVER_DOMAIN + item.attributes.icon.data.attributes.url
                    }
                    style={{ width: 25, aspectRatio: 1, borderRadius: 20 }}
                  />
                }
                iconPosition="start"
              />
            );
          })}
        </Tabs>

        {langs?.map((item, index) => {
          return (
            <TabPanel value={index.toString()} key={index}>
              <SuccessAlert show={success} setShow={(v) => setSuccess(v)} />
              <ErrorAlert show={fail} setShow={(v) => setFail(v)} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 25,
                }}
              >
                <div className="flex-col gap-5 w-fit">
                  <h3>Generate and Download a new Sitemap</h3>
                  <img
                    className="w-40 aspect-square object-cover"
                    src={require("../assets/icons/sitemap-icon.png")}
                  />
                </div>
              </div>

              {user.perms[24].edit && (
                <div className="submitBTN" onClick={() => generate(index)}>
                  Generate Sitemap
                </div>
              )}
            </TabPanel>
          );
        })}
      </Box>
    </TabContext>
  );
}
